import { useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { KEY_RECOIL } from '@/shared/config';
import {
  selectActiveTab,
  selectEventChanges,
  selectEventNews,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { NewEventsProps } from './newEvents';

/**
 * Backs the dialog listing the rhythm events created locally on the ECG events tab. Exposes the new events, thumbnail render settings and current selection from the report store, confirms strip removal through a ref-held index before dropping the event and the event changes it linked, and on row click switches the active button, selected strip and jump date to that event.
 *
 * @param props - Dialog inputs carrying the onCancel callback.
 * @returns An object carrying keyRecoil, eventNews, selectedStrip, activeButton, the thumbnail settings, the confirm-remove flag and its setter, stripRemoveIndex and the four click handlers.
 */
const useNewEvents = ({ onCancel }: Pick<NewEventsProps, 'onCancel'>) => {
  const keyRecoil = KEY_RECOIL.RHYTHM_EVENT;
  const eventNews = useReportStore(selectEventNews(keyRecoil));
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Stores the locally created rhythm events for the ECG events tab. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New list of new events, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the pending rhythm-event edits for the ECG events tab out of the report store. Applies the TAB_2-keyed event-changes selector to a one-off store snapshot instead of subscribing, so the caller sees current data without triggering a re-render.
   *
   * @returns The edited events awaiting save.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Stores the edited rhythm events for the ECG events tab. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New event-changes value, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Stores the grouped event snapshot that the undo and redo stack replays for the ECG events tab. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New event options value, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Stores whether the ECG viewer has no data to show for the events tab. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New empty-data flag, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setReloadEcgViewerAction = useReportStore((s) => s.actions.setReloadEcgViewer);
  /**
   * Stores the counter whose change forces the ECG viewer to reload. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New reload counter, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setReloadEcgViewer = (v: Parameters<typeof setReloadEcgViewerAction>[1]) =>
    setReloadEcgViewerAction(keyRecoil, v);
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Stores the date currently chosen in the events tab date selector. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New selected date value, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Stores which strip is selected on the ECG events tab. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New selected strip, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setActiveButtonAction = useReportStore((s) => s.actions.setActiveTab);
  /**
   * Stores which rhythm, beat or heart-rate button is active on the ECG events tab. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New active button, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setActiveButton = (v: Parameters<typeof setActiveButtonAction>[1]) =>
    setActiveButtonAction(keyRecoil, v);
  const channelsThumbnail = useReportStore((s) => s.channelsThumbnail);
  const highPassThumbnail = useReportStore((s) => s.highPassThumbnail);
  const gainThumbnail = useReportStore((s) => s.gainThumbnail);
  const [isOpenConfirmRemove, setIsOpenConfirmRemove] = useState(false);
  const stripRemoveIndex = useRef(undefined);
  const setIsViewNewEventAction = useReportStore((s) => s.actions.setIsViewNewEvent);
  /**
   * Stores whether the viewer is displaying a locally created event. Forwards the boolean to the store action with the TAB_2 key already bound.
   *
   * @param v - True while a new event is being viewed.
   * @returns Nothing.
   */
  const setIsViewNewEvent = (v: boolean) => setIsViewNewEventAction(keyRecoil, v);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Stores the epoch timestamp the ECG viewer should scroll to. Forwards the value to the store action with the TAB_2 key already bound.
   *
   * @param v - New jump date, or an updater applied to the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);

  /**
   * Dismisses the dialog listing locally created events. Invokes the onCancel callback supplied in props.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    onCancel();
  };

  /**
   * Starts removal of one strip from the list of newly created events. Records the row index in a ref and opens the confirmation dialog rather than deleting the event immediately.
   *
   * @param index - Position of the new event whose strip is being removed.
   * @returns Nothing.
   */
  const onClickRemoveStrips = (index: number) => {
    stripRemoveIndex.current = index;
    setIsOpenConfirmRemove(true);
  };

  /**
   * Deletes the confirmed new event together with the event changes it created. Clones the new-events list, drops the row at the stored index, filters out event changes matching that event linkEvents, records both arrays in the undo and redo snapshot, writes them back to the store, clears the pending index, closes the confirmation and nudges the ECG viewer to reload.
   *
   * @returns Nothing.
   */
  const onClickConfirmRemoveStrip = () => {
    const updatedEventNews = _.cloneDeep(eventNews);
    const linkEvents = updatedEventNews[stripRemoveIndex.current]?.linkEvents || [];
    const filterEventChanges = getEventChanges().filter((eventChange: any) => {
      if (linkEvents.find((event: any) => event.idEvent === eventChange.idEvent)) {
        return false;
      }
      return true;
    });
    updatedEventNews.splice(stripRemoveIndex.current, 1);
    // Perform grouping event action applicable to undo/redo
    setEventOptions((prevOptions: any) => ({
      ...prevOptions,
      eventNewsState: updatedEventNews,
      eventChangesState: filterEventChanges,
    }));
    setEventNews(updatedEventNews);
    setEventChanges(filterEventChanges);
    stripRemoveIndex.current = undefined;
    setIsOpenConfirmRemove(false);
    setReloadEcgViewer((prev: any) => prev + _.round(Math.random() * 100));
  };

  /**
   * Selects one locally created event so the ECG viewer shows it. Skips when that event is already selected, otherwise clears the selected date, sets the active button to the event type, marks the strip selected, clears the empty-data flag, flags the new-event view and jumps the viewer to the event start.
   *
   * @param event - The newly created event row that was clicked.
   * @returns Nothing.
   */
  const handleClickSelectEvent = (event: any) => {
    if (event.id !== selectedStrip?.id) {
      setSelectedDateValue(null);
      setActiveButton(event.type);
      setSelectedStrip(event);
      setIsEmptyData(false);
      setIsViewNewEvent(true);
      setJumpDate(moment(event?.start).valueOf());
    }
  };

  return {
    keyRecoil,
    eventNews,
    selectedStrip,
    activeButton,
    channelsThumbnail,
    highPassThumbnail,
    gainThumbnail,
    isOpenConfirmRemove,
    setIsOpenConfirmRemove,
    stripRemoveIndex,
    onClickCancel,
    onClickRemoveStrips,
    onClickConfirmRemoveStrip,
    handleClickSelectEvent,
  };
};

export default useNewEvents;
